import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useContainerQuery } from '@backrs_ui/common-ui';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

export type RoadmapTimelineItem = Readonly<{
  id: string;
  /** Short label on the rail, e.g. 'April'. */
  railLabel: string;
  title: string;
  overview: string;
  badge?: string;
  /** Sticker illustration shown inside the rail card. */
  stickerSrc?: string;
  detail: React.ReactNode;
}>;

type RoadmapTimelineProps = Readonly<{
  idPrefix: string;
  items: readonly RoadmapTimelineItem[];
}>;

const COMPACT_BREAKPOINT = 600;

/** Height of the rail label row, used to center the timeline dot against the label. */
const RAIL_LABEL_HEIGHT = '24px';

/** Upper bound on a smooth scroll, after which a clicked card is treated as reached. */
const SCROLL_SETTLE_MS = 1200;

/**
 * Two-column guide layout: a vertical timeline rail of summary cards on the left and the
 * active item's copy on the right. The rail tracks the copy as it scrolls, and a card click
 * scrolls its copy into view. Below the breakpoint each card is followed by its own copy.
 */
export const RoadmapTimeline: React.FC<RoadmapTimelineProps> = ({ idPrefix, items }) => {
  const [containerRef, isCompact] = useContainerQuery<HTMLDivElement>(COMPACT_BREAKPOINT);
  const railRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  /** Item a card click is scrolling toward; nothing is active until we arrive. */
  const pendingIdRef = useRef<string | null>(null);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const detailId = useCallback((id: string) => `${idPrefix}-detail-${id}`, [idPrefix]);

  useEffect(() => () => clearTimeout(pendingTimerRef.current), []);

  const itemIdsKey = useMemo(() => items.map((item) => item.id).join('|'), [items]);

  useEffect(() => {
    if (isCompact) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const topmostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (!topmostVisible) return;

        const visibleId = topmostVisible.target.id.replace(`${idPrefix}-detail-`, '');

        // While scrolling to a clicked card, ignore everything it passes over.
        if (pendingIdRef.current) {
          if (visibleId !== pendingIdRef.current) return;

          pendingIdRef.current = null;
          clearTimeout(pendingTimerRef.current);
        }

        setActiveId(visibleId);
      },
      { rootMargin: '-25% 0px -65% 0px' },
    );

    itemIdsKey.split('|').forEach((id) => {
      const el = document.getElementById(detailId(id));

      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [detailId, idPrefix, isCompact, itemIdsKey]);

  // Open on the item a deep link points at, e.g. '#guide-detail-april'.
  useEffect(() => {
    const hashId = window.location.hash.slice(1);
    const item = itemIdsKey.split('|').find((id) => detailId(id) === hashId);

    if (!item) return;

    setActiveId(item);
    document.getElementById(hashId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [detailId, itemIdsKey]);

  // Nudge the rail only when the active card has left it, so it stays still while the
  // page scrolls. scrollTo on the rail keeps page scroll untouched, unlike scrollIntoView.
  useEffect(() => {
    const rail = railRef.current;
    const card = cardRefs.current[activeId];

    if (!rail || !card || rail.scrollHeight <= rail.clientHeight) return;

    const cardTop = card.offsetTop;
    const cardBottom = cardTop + card.clientHeight;

    if (cardTop >= rail.scrollTop && cardBottom <= rail.scrollTop + rail.clientHeight) return;

    const target = cardTop < rail.scrollTop ? cardTop : cardBottom - rail.clientHeight;

    rail.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
  }, [activeId]);

  const handleCardClick = useCallback(
    (id: string) => {
      pendingIdRef.current = id;

      // The last items can't reach the spy's trigger zone, so settle on the target regardless.
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = setTimeout(() => {
        if (pendingIdRef.current !== id) return;

        pendingIdRef.current = null;
        setActiveId(id);
      }, SCROLL_SETTLE_MS);

      document.getElementById(detailId(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [detailId],
  );

  const renderCard = (item: RoadmapTimelineItem, isLast: boolean) => {
    // Scroll spy only runs in the two-column layout, so nothing is "active" when stacked.
    const isActive = !isCompact && item.id === activeId;

    return (
      <Box key={item.id} sx={railGroupSx}>
        <Box sx={railRowSx}>
          <Box sx={railMarkerSx}>
            <Box sx={dotAlignSx}>
              <Box sx={dotSx(isActive)} />
            </Box>
            {!isLast && <Box sx={connectorSx(isActive)} />}
          </Box>
          <Box sx={railContentSx}>
            <Box sx={railLabelRowSx}>
              <Typography variant="text-sm-medium" sx={railLabelSx}>
                {item.railLabel}
              </Typography>
              {item.badge && <Chip label={item.badge} variant="tag" size="sm" sx={badgeSx} />}
            </Box>
            <Box
              ref={(el: HTMLDivElement | null) => {
                cardRefs.current[item.id] = el;
              }}
              id={`${idPrefix}-card-${item.id}`}
              onClick={() => handleCardClick(item.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleCardClick(item.id);
                }
              }}
              sx={cardSx(isActive)}
            >
              <Typography variant="text-sm-semibold" sx={cardTitleSx}>
                {item.title}
              </Typography>
              <Box sx={cardOverviewRowSx}>
                <Typography variant="text-sm-regular" sx={cardOverviewSx}>
                  {item.overview}
                </Typography>
                {item.stickerSrc && (
                  <Box component="img" src={item.stickerSrc} alt="" sx={stickerSx} aria-hidden />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  if (isCompact) {
    return (
      <Box ref={containerRef} sx={compactSx}>
        {items.map((item, index) => (
          <Box key={item.id} id={detailId(item.id)} sx={compactItemSx}>
            {renderCard(item, index === items.length - 1)}
            {item.detail}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={layoutSx}>
      <Box sx={railColumnSx}>
        <Box ref={railRef} sx={railScrollSx}>
          {items.map((item, index) => renderCard(item, index === items.length - 1))}
        </Box>
      </Box>

      <Box sx={detailColumnSx}>
        {items.map((item) => (
          <Box key={item.id} id={detailId(item.id)} sx={detailSx}>
            {item.detail}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const layoutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
});

const railColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  flex: '0 0 300px',
  position: 'sticky',
  top: backrs.spacing['spacing-xl'],
});

const railScrollSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Offset leaves room for the app header sitting above the sticky rail.
  maxHeight: 'calc(100vh - 160px)',
  overflowY: 'auto',
  pr: backrs.spacing['spacing-xs'],
  scrollbarWidth: 'thin',
});

const railGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const railRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: backrs.spacing['spacing-sm'],
});

const railContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  flex: 1,
  minWidth: 0,
  pb: backrs.spacing['spacing-xl'],
});

const railLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const railMarkerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: '0 0 auto',
};

const dotAlignSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  height: RAIL_LABEL_HEIGHT,
};

const dotSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '14px',
    height: '14px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: isActive
      ? backrs.colors['Brand/500']
      : backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    flex: '0 0 auto',
  });

const connectorSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: isActive ? '3px' : '2px',
    flex: 1,
    backgroundColor: isActive ? backrs.colors['Brand/500'] : backrs.colors['Brand/200'],
  });

const cardSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xxs'],
    flex: 1,
    minWidth: 0,
    cursor: 'pointer',
    p: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-lg'],
    backgroundColor: backrs.colors['Colors/Base/White'],
    outline: `1px solid ${
      isActive ? backrs.colors['Brand/500'] : backrs.colors['Colors/Border/border-secondary']
    }`,
    outlineOffset: '-1px',
    boxShadow: 'none',
    transition: 'box-shadow 0.3s ease-in-out, outline-color 0.3s ease-in-out',
    '&:hover': {
      outline: `1px solid ${backrs.colors['Colors/Base/Black']}`,
      outlineOffset: '-1px',
      boxShadow: backrs.shadows['shadow-md'],
    },
  });

const cardOverviewRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const cardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const railLabelRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  height: RAIL_LABEL_HEIGHT,
});

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  height: 'auto',
  px: backrs.spacing['spacing-sm'],
  py: backrs.spacing['spacing-xxs'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Colors/Text/text-brand-primary'],
});

const cardOverviewSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const stickerSx: SxProps<Theme> = {
  width: '72px',
  height: 'auto',
  flex: '0 0 auto',
};

const detailColumnSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-7xl'],
  flex: 1,
  minWidth: 0,
});

const detailSx: SxProps<Theme> = {
  scrollMarginTop: '96px', // Clears the sticky app header.
};

const compactSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-5xl'],
  width: '100%',
});

const compactItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  scrollMarginTop: '96px', // Clears the sticky app header.
});
